import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { ProjectsContext } from '../contexts/ProjectsContext';
import { navItems } from '../constants/navItems';
import { MAX_CONTENT_WIDTH } from '../constants/layout';
import { getProjectSlug } from '../utils/slugify';

function NavigationBar() {
  const location = useLocation();
  const { projects, categories } = useContext(ProjectsContext);
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContactHovered, setIsContactHovered] = useState(false);

  // Group projects by category
  const projectsByCategory = projects.reduce((acc, project) => {
    const categoryId = project.development_category_id || 'uncategorized';
    if (!acc[categoryId]) {
      acc[categoryId] = [];
    }
    acc[categoryId].push(project);
    return acc;
  }, {});

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        window.innerWidth <= 768 ||
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Helper to see if a given path is "active"
  const isActive = (path) => {
    return location.pathname === path;
  };

  const linkStyle = (path, overrides = {}) => {
    const active = isActive(path);
    return {
      textDecoration: 'none',
      color: active
        ? 'var(--color-navbar-pill-text)'
        : 'var(--color-navbar-text)',
      fontWeight: '700',
      padding: '8px 16px',
      borderRadius: '0',
      backgroundColor: active
        ? 'var(--color-navbar-pill-bg)'
        : 'transparent',
      transition: 'all 0.2s ease',
      display: 'block',
      ...overrides,
    };
  };

  const [isMenuHovered, setIsMenuHovered] = useState(false);

  const contactButtonStyle = {
    textDecoration: 'none',
    color: isContactHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    backgroundColor: isContactHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    borderRadius: '0',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
  };

  const menuButtonStyle = {
    border: 'none',
    backgroundColor: isMenuHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isMenuHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '0',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
    padding: 0,
  };

  const brandLinkStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    textDecoration: 'none',
  };
  const storeTitleStyle = {
    color: 'var(--color-text-primary)',
    fontSize: '20px',
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontWeight: '700',
    letterSpacing: '-0.02em',
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Navigation */}
        <nav
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'var(--color-navbar-surface)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 100,
            borderBottom: '1px solid var(--color-navbar-border)',
          }}
        >
          <div
            className="nav-safe-area"
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              maxWidth: MAX_CONTENT_WIDTH,
              margin: '0 auto',
              width: '100%',
            }}
          >
            {/* Hamburger Menu Button - Left */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={menuButtonStyle}
              aria-label="Toggle menu"
              onMouseEnter={() => setIsMenuHovered(true)}
              onMouseLeave={() => setIsMenuHovered(false)}
              onFocus={() => setIsMenuHovered(true)}
              onBlur={() => setIsMenuHovered(false)}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-around',
                  width: '18px',
                  height: '18px',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'var(--color-navbar-icon)',
                    transition: 'all 0.3s ease',
                    transform: isMenuOpen
                      ? 'rotate(45deg) translate(4px, 4px)'
                      : 'none',
                  }}
                />
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'var(--color-navbar-icon)',
                    transition: 'all 0.3s ease',
                    opacity: isMenuOpen ? 0 : 1,
                  }}
                />
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'var(--color-navbar-icon)',
                    transition: 'all 0.3s ease',
                    transform: isMenuOpen
                      ? 'rotate(-45deg) translate(5px, -5px)'
                      : 'none',
                  }}
                />
              </div>
            </button>

            {/* Logo - Center */}
            <Link
              to="/"
              style={brandLinkStyle}
            >
              <span style={storeTitleStyle}>Davinese</span>
            </Link>

            {/* Get in Touch - Right */}
            <Link
              to="/contact"
              style={contactButtonStyle}
              onMouseEnter={() => setIsContactHovered(true)}
              onMouseLeave={() => setIsContactHovered(false)}
            >
              <MailOutlineIcon style={{ fontSize: '20px' }} />
            </Link>
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99,
              animation: 'fadeIn 0.2s ease',
            }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Mobile Menu Panel (translucent background) */}
        <div
          className="sidebar-safe-area"
          style={{
            position: 'fixed',
            top: 0,
            left: isMenuOpen ? 0 : '-280px',
            width: '280px',
            height: '100vh',
            backgroundColor: 'var(--color-mobile-menu-surface)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 101,
            transition: 'left 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: '56px', // Space for the nav bar
            fontFamily: 'Helvetica, Arial, sans-serif',
          }}
        >
          <div
            style={{
              padding: '16px',
              overflowY: 'auto',
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    ...linkStyle(item.path),
                    marginBottom: '8px',
                    padding: '12px 16px',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Categorized Projects */}
            {categories.map(category => {
              const categoryProjects = projectsByCategory[category.id] || [];
              if (categoryProjects.length === 0) return null;

              return (
                <div
                  key={category.id}
                  style={{
                    paddingTop: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: '12px',
                      color: 'var(--color-navbar-text)',
                      opacity: 0.7,
                      paddingLeft: '16px',
                    }}
                  >
                    {category.name}
                  </span>
                  {categoryProjects.map(project => {
                    const label = project.name || 'Untitled Project';
                    const projectSlug = getProjectSlug(project);
                    return (
                      <Link
                        key={project.id}
                        to={`/${projectSlug}`}
                        style={{
                          ...linkStyle(`/${projectSlug}`),
                          marginBottom: '8px',
                          padding: '12px 16px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        {label}
                      </Link>
                    );
                  })}
                </div>
              );
            })}

            {/* Uncategorized Projects */}
            {projectsByCategory['uncategorized'] && projectsByCategory['uncategorized'].length > 0 && (
              <div
                style={{
                  paddingTop: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '12px',
                    color: 'var(--color-navbar-text)',
                    opacity: 0.7,
                    paddingLeft: '16px',
                  }}
                >
                  More
                </span>
                {projectsByCategory['uncategorized'].map(project => {
                  const label = project.name || 'Untitled Project';
                  const projectSlug = getProjectSlug(project);
                  return (
                    <Link
                      key={project.id}
                      to={`/${projectSlug}`}
                      style={{
                        ...linkStyle(`/${projectSlug}`),
                        marginBottom: '8px',
                        padding: '12px 16px',
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      {label}
                    </Link>
                  );
                })}
              </div>
            )}

          </div>
        </div>

        {/* Add fadeIn animation */}
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}</style>
      </>
    );
  }

  // Desktop Navigation (hamburger + logo + search)
  return (
    <>
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: 'var(--color-navbar-surface)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 100,
          borderBottom: '1px solid var(--color-navbar-border)',
        }}
      >
        <div
          className="nav-safe-area"
          style={{
            maxWidth: MAX_CONTENT_WIDTH,
            margin: '0 auto',
            padding: '14px 16px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Hamburger Menu - Left Side */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={menuButtonStyle}
            aria-label="Toggle menu"
            onMouseEnter={() => setIsMenuHovered(true)}
            onMouseLeave={() => setIsMenuHovered(false)}
            onFocus={() => setIsMenuHovered(true)}
            onBlur={() => setIsMenuHovered(false)}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                width: '18px',
                height: '18px',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(45deg) translate(4px, 4px)'
                    : 'none',
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  opacity: isMenuOpen ? 0 : 1,
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(-45deg) translate(5px, -5px)'
                    : 'none',
                }}
              />
            </div>
          </button>

          {/* Logo - Center */}
          <Link
            to="/"
            style={{
              ...brandLinkStyle,
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <span style={storeTitleStyle}>Davinese</span>
          </Link>

          {/* Get in Touch - Right Side */}
          <Link
            to="/contact"
            style={contactButtonStyle}
            onMouseEnter={() => setIsContactHovered(true)}
            onMouseLeave={() => setIsContactHovered(false)}
          >
            <MailOutlineIcon style={{ fontSize: '20px' }} />
          </Link>
        </div>
      </nav>

      {/* Desktop Menu Overlay */}
      {isMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99,
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Desktop Menu Panel */}
      <div
        className="sidebar-safe-area"
        style={{
          position: 'fixed',
          top: 0,
          left: isMenuOpen ? 0 : '-320px',
          width: '320px',
          height: '100vh',
          backgroundColor: 'var(--color-mobile-menu-surface)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 101,
          transition: 'left 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '56px',
          fontFamily: 'Helvetica, Arial, sans-serif',
        }}
      >
        <div
          style={{
            padding: '16px',
            overflowY: 'auto',
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  ...linkStyle(item.path),
                  marginBottom: '8px',
                  padding: '12px 16px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Categorized Projects */}
          {categories.map(category => {
            const categoryProjects = projectsByCategory[category.id] || [];
            if (categoryProjects.length === 0) return null;

            return (
              <div
                key={category.id}
                style={{
                  paddingTop: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '12px',
                    color: 'var(--color-navbar-text)',
                    opacity: 0.7,
                    paddingLeft: '16px',
                  }}
                >
                  {category.name}
                </span>
                {categoryProjects.map(project => {
                  const label = project.name || 'Untitled Project';
                  const projectSlug = getProjectSlug(project);
                  return (
                    <Link
                      key={project.id}
                      to={`/${projectSlug}`}
                      style={{
                        ...linkStyle(`/${projectSlug}`),
                        marginBottom: '8px',
                        padding: '12px 16px',
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      {label}
                    </Link>
                  );
                })}
              </div>
            );
          })}

          {/* Uncategorized Projects */}
          {projectsByCategory['uncategorized'] && projectsByCategory['uncategorized'].length > 0 && (
            <div
              style={{
                paddingTop: '16px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '12px',
                  color: 'var(--color-navbar-text)',
                  opacity: 0.7,
                  paddingLeft: '16px',
                }}
              >
                More
              </span>
              {projectsByCategory['uncategorized'].map(project => {
                const label = project.name || 'Untitled Project';
                const projectSlug = getProjectSlug(project);
                return (
                  <Link
                    key={project.id}
                    to={`/${projectSlug}`}
                    style={{
                      ...linkStyle(`/${projectSlug}`),
                      marginBottom: '8px',
                      padding: '12px 16px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
          )}

        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}

export default NavigationBar;
